import { Grid } from '@material-ui/core';
import React, { FC, ReactNode } from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { Link, useParams } from 'react-router-dom';

import { StageCard, StageCardProps } from './StageCard';
import { useStyles } from './styles';

type UrlParams = {
  accountSlug: string;
  funnelSlug: string;
};

export interface DraggableStageCardProps extends StageCardProps {
  // Rendered inside the same draggable node as the stage card, outside the
  // Link, so react-beautiful-dnd moves it together with the stage during a
  // drag and its own controls don't trigger navigation.
  complianceUpsell?: ReactNode;
}

export const DraggableStageCard: FC<DraggableStageCardProps> = ({
  complianceUpsell,
  onDiscardChanges,
  refetchStages,
  scheduledApplicants,
  selected,
  stage,
}) => {
  const classes = useStyles();

  const { accountSlug, funnelSlug } = useParams<UrlParams>();

  const isDragDisabled =
    stage.type === 'ArchivedStage' || stage.type === 'RejectedStage';

  return (
    <Draggable
      draggableId={stage.external_id}
      index={stage.position}
      isDragDisabled={isDragDisabled}
    >
      {provided => (
        <Grid
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={classes.draggableStageWrapper}
        >
          {/*
           * dragHandleProps (not draggableProps) is what wires up react-beautiful-dnd's
           * keyboard sensor, binding Space/Enter on this element to lift the draggable.
           * Scoped to just the card face rather than the wrapping Grid above so that
           * `complianceUpsell`'s own controls (e.g. Discover/Dismiss) aren't inside that
           * listener's subtree — otherwise a Space press on one of them bubbles up and
           * starts a keyboard drag instead of activating the control.
           *
           * `role={undefined}` drops the `role="button"` those props carry, which fits
           * the plain div a drag handle usually is. This handle is the stage Link, and a
           * screen reader announcing a button that navigates gives the wrong affordance.
           * The sensor reads its handles from the data-rbd-drag-handle-* attributes, so
           * the role is advisory here and safe to hand back to the anchor.
           */}
          <Grid
            {...provided.dragHandleProps}
            role={undefined}
            component={Link}
            className={classes.stageCardContainer}
            to={`/${accountSlug}/openings/${funnelSlug}/workflow/${stage.slug}`}
          >
            <StageCard
              onDiscardChanges={onDiscardChanges}
              refetchStages={refetchStages}
              scheduledApplicants={scheduledApplicants}
              selected={selected}
              stage={stage}
            />
          </Grid>
          {complianceUpsell}
        </Grid>
      )}
    </Draggable>
  );
};
